import { useState, useEffect } from 'react';
import { adminGet } from '../lib/api';

interface Jurisdiction {
  stateCode: string; uplRef: string; uplShort: string;
  nilRef: string; nilEffective: string; nilProvisions: string;
  agentRequired: boolean; agentStatute: string | null;
  hsNilPermitted: boolean; hsAssociation: string; active: boolean;
}

const STATE_NAMES: Record<string, string> = {
  CA: 'California', TX: 'Texas', FL: 'Florida', AZ: 'Arizona', AR: 'Arkansas',
  AL: 'Alabama', NY: 'New York', GA: 'Georgia', TN: 'Tennessee', NM: 'New Mexico',
};

export function JurisdictionEditor() {
  const [jurisdictions, setJurisdictions] = useState<Jurisdiction[]>([]);
  const [selected, setSelected] = useState<Jurisdiction | null>(null);

  useEffect(() => {
    (async () => {
      const resp = await adminGet('/api/admin/jurisdictions');
      if (resp.ok && resp.data) setJurisdictions((resp.data as { jurisdictions: Jurisdiction[] }).jurisdictions || []);
    })();
  }, []);

  if (selected) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setSelected(null)} className="text-dark-400 hover:text-white"><span className="material-symbols-outlined">arrow_back</span></button>
          <h1 className="text-2xl font-display font-bold text-white">{STATE_NAMES[selected.stateCode] || selected.stateCode}</h1>
          <span className="px-2 py-0.5 text-xs bg-dark-600 text-dark-300 rounded font-mono">{selected.stateCode}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
            <h3 className="text-white font-medium mb-4">UPL Reference</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-dark-400 block text-xs">Statute</span><span className="text-dark-300 font-mono text-xs">{selected.uplRef}</span></div>
              <div><span className="text-dark-400 block text-xs">Description</span><span className="text-dark-300">{selected.uplShort}</span></div>
            </div>
          </div>
          <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
            <h3 className="text-white font-medium mb-4">NIL Law</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-dark-400 block text-xs">Statute</span><span className="text-dark-300 font-mono text-xs">{selected.nilRef}</span></div>
              <div><span className="text-dark-400 block text-xs">Effective</span><span className="text-dark-300">{selected.nilEffective}</span></div>
              <div><span className="text-dark-400 block text-xs">Key Provisions</span><span className="text-dark-300">{selected.nilProvisions}</span></div>
            </div>
          </div>
          <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
            <h3 className="text-white font-medium mb-4">Agent Registration</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-dark-400 block text-xs">Required</span><span className={selected.agentRequired ? 'text-severity-medium' : 'text-severity-low'}>{selected.agentRequired ? 'Yes' : 'No'}</span></div>
              {selected.agentStatute && <div><span className="text-dark-400 block text-xs">Statute</span><span className="text-dark-300 font-mono text-xs">{selected.agentStatute}</span></div>}
            </div>
          </div>
          <div className="bg-dark-700 rounded-2xl border border-dark-650/50 p-6">
            <h3 className="text-white font-medium mb-4">High School NIL</h3>
            <div className="space-y-3 text-sm">
              <div><span className="text-dark-400 block text-xs">Permitted</span><span className={selected.hsNilPermitted ? 'text-severity-low' : 'text-severity-high'}>{selected.hsNilPermitted ? 'Yes' : 'No'}</span></div>
              <div><span className="text-dark-400 block text-xs">Association</span><span className="text-dark-300">{selected.hsAssociation}</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-display font-bold text-white mb-6">Jurisdictions</h1>
      <div className="bg-dark-700 rounded-2xl border border-dark-650/50 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dark-650/50">
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">State</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">UPL Reference</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">NIL Statute</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Agent Reg.</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">HS NIL</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-650/30">
            {jurisdictions.map(j => (
              <tr key={j.stateCode} onClick={() => setSelected(j)} className="hover:bg-dark-750/50 cursor-pointer transition-colors">
                <td className="px-4 py-3"><div className="flex items-center gap-2"><span className="px-2 py-0.5 text-xs bg-dark-800 text-dark-300 rounded font-mono">{j.stateCode}</span><span className="text-sm text-white">{STATE_NAMES[j.stateCode]}</span></div></td>
                <td className="px-4 py-3 text-xs text-dark-400 font-mono">{j.uplRef?.substring(0, 30)}...</td>
                <td className="px-4 py-3 text-xs text-dark-400">{j.nilRef?.substring(0, 25)}...</td>
                <td className="px-4 py-3"><span className={`text-xs ${j.agentRequired ? 'text-severity-medium' : 'text-dark-500'}`}>{j.agentRequired ? 'Required' : 'No'}</span></td>
                <td className="px-4 py-3"><span className={`text-xs ${j.hsNilPermitted ? 'text-severity-low' : 'text-severity-high'}`}>{j.hsNilPermitted ? 'Yes' : 'No'}</span></td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 text-xs bg-severity-low/10 text-severity-low rounded">Active</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
